/**
 * 场景详情（点击场景名，从右侧滑出）：
 * 各通道差异曲线（echarts，按需加载）、关键指标（最差 2 秒 / 最大值）、两侧事件、
 * 强事件、评估器判定失败时刻「前 3 秒 / 后 2 秒」窗口值。
 */
import { Alert, Button, Descriptions, Drawer, Empty, Spin, Table, Tag } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';

import {
  getBehaviorCompareSceneDetail,
  type BehaviorSceneDetail,
  type BehaviorSceneRow,
} from '@/apis/simulation';

import { METRIC_NAME, formatDiff, labelName, terminationReasonName, type FailWindowLike } from '../format';
import EncounterTable from './EncounterTable';
import FailWindowChart from './FailWindowChart';
import { LabelTag, VerdictTag, useSideBySide } from './Shared';

interface Props {
  compareId: number;
  row: BehaviorSceneRow | null;
  onClose: () => void;
}

const CHANNELS: { key: string; name: string; unit: string }[] = [
  { key: 'pos', name: '位置差', unit: 'm' },
  { key: 'lat', name: '横向差', unit: 'm' },
  { key: 'lon', name: '纵向差', unit: 'm' },
  { key: 'yaw', name: '朝向差', unit: '°' },
  { key: 'speed', name: '速度差', unit: 'm/s' },
  { key: 'accel', name: '加速度差', unit: 'm/s²' },
];

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

export default function SceneDrawer({ compareId, row, onClose }: Props) {
  const [detail, setDetail] = useState<BehaviorSceneDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sideBySide = useSideBySide();

  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    setDetail(null);
    setError(null);
    setLoading(true);
    getBehaviorCompareSceneDetail(compareId, row.sceneId)
      .then((d) => alive && setDetail(d))
      .catch((e) => alive && setError(e instanceof Error ? e.message : '加载场景详情失败'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [compareId, row]);

  const series = detail?.detail?.series;
  const chartOption = useMemo(() => {
    if (!series?.t?.length) return null;
    const t = series.t;
    const present = CHANNELS.filter((c) => Array.isArray(series[c.key]));
    return {
      grid: { left: 52, right: 24, top: 36, bottom: 56 },
      tooltip: { trigger: 'axis' },
      legend: { top: 4, data: present.map((c) => c.name), selected: Object.fromEntries(present.map((c) => [c.name, c.key === 'pos' || c.key === 'speed'])) },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
      xAxis: { type: 'category', data: t.map((x) => x.toFixed(1)), name: '秒' },
      yAxis: { type: 'value', scale: true },
      series: present.map((c) => ({
        name: c.name, type: 'line', showSymbol: false, data: series[c.key], sampling: 'lttb',
      })),
    };
  }, [series]);

  const worst = asRecord(asRecord(detail?.scores).worst2s);
  const maxes = asRecord(asRecord(detail?.scores).max);
  const events = asRecord(detail?.facts?.events);
  const stops = asRecord(events.stops);
  const junctions = asRecord(events.junctions);
  const termination = asRecord(events.termination);

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={880}
      destroyOnHidden
      title={
        row ? (
          <div className="bc-drawer-title">
            <span>{row.sceneName ?? row.sceneId}</span>
            <LabelTag label={row.label} />
            <Button size="small" onClick={() => sideBySide.open(row)} loading={sideBySide.loadingId === row.sceneId}>
              并排回放
            </Button>
          </div>
        ) : null
      }
    >
      {loading ? <Spin style={{ display: 'block', margin: '40px auto' }} /> : null}
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {detail ? (
        <div className="bc-drawer-body">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="行为结论">{labelName(detail.label)}{detail.verdictMismatch ? '（两侧结论不一致）' : ''}</Descriptions.Item>
            <Descriptions.Item label="平台结论">
              当前 <VerdictTag v={detail.curVerdict} /> / 基线 <VerdictTag v={detail.baseVerdict} />
            </Descriptions.Item>
            <Descriptions.Item label="当前 task">{detail.curTaskId ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="基线 task">{detail.baseTaskId ?? '—'}</Descriptions.Item>
          </Descriptions>

          {detail.strongEvents.length > 0 ? (
            <div className="bc-section">
              <div className="bc-section-title">强事件</div>
              {detail.strongEvents.map((e) => (
                <Tag key={e.code} color="red" bordered={false}>
                  {e.name}{e.detail ? ` ${e.detail}` : ''}
                </Tag>
              ))}
            </div>
          ) : null}

          <div className="bc-section">
            <div className="bc-section-title">各通道差异曲线（当前 − 基线，闭环段）</div>
            {chartOption ? (
              <ReactECharts option={chartOption} style={{ height: 300 }} notMerge />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该场景没有可画的曲线（无闭环对比窗口）" />
            )}
          </div>

          <div className="bc-section">
            <div className="bc-section-title">关键指标</div>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={['pos', 'speed', 'yaw', 'accel'].map((k) => ({
                key: k, name: METRIC_NAME[k], worst: worst[k], max: maxes[k],
              }))}
              columns={[
                { title: '指标', dataIndex: 'name' },
                { title: '最差 2 秒', dataIndex: 'worst', render: (v, r) => formatDiff(r.key, v as number | null) },
                { title: '最大值', dataIndex: 'max', render: (v, r) => formatDiff(r.key, v as number | null) },
              ]}
            />
          </div>

          <div className="bc-section">
            <div className="bc-section-title">事件对比</div>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="终止原因（基线）">{terminationReasonName(termination.base as string)}</Descriptions.Item>
              <Descriptions.Item label="终止原因（当前）">{terminationReasonName(termination.cur as string)}</Descriptions.Item>
              <Descriptions.Item label="停车次数">{String(stops.count_base ?? '—')} → {String(stops.count_cur ?? '—')}</Descriptions.Item>
              <Descriptions.Item label="进入路口次数">{String(junctions.count_base ?? '—')} → {String(junctions.count_cur ?? '—')}</Descriptions.Item>
            </Descriptions>
          </div>

          <div className="bc-section">
            <div className="bc-section-title">他车关系（共有障碍物）</div>
            <EncounterTable
              facts={detail.facts}
              movement={detail.detail?.movement as Record<string, unknown> | undefined}
              posMax={(maxes.pos as number | undefined) ?? null}
            />
          </div>

          <div className="bc-section">
            <div className="bc-section-title">评估器判定失败时刻（前 3 秒 / 后 2 秒）</div>
            {detail.failWindows.length === 0 ? (
              <span className="bc-muted">没有失败窗口数据（该场景当前侧没有评估器失败事件，或管线尚未产出该项）</span>
            ) : (
              detail.failWindows.map((w, i) => (
                <FailWindowChart key={`${(w as { evaluator?: string }).evaluator}-${i}`} w={w as unknown as FailWindowLike} />
              ))
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
